/**
 * Naming a translation, given the ones already on its norm set.
 *
 * The suggested name is a pure function of the audience, and the table has
 * `UNIQUE (perception_model_project_id, name)`. So two translations with the
 * same audience on one norm set collide by construction — which is not a
 * mistake to prevent but a real thing to want: the same audience over a
 * different set of products is a different translation.
 */

/** How the constraint reports itself, whatever the generated name happens to be. */
const DUPLICATE = /duplicate key value violates unique constraint/i;

/**
 * The suggested name, made unique against its siblings.
 *
 * Suffixes rather than refuses: the analyst asked for this audience, and the
 * name is a label rather than the thing being defined.
 */
export const uniqueName = (base: string, taken: Iterable<string>): string => {
  const used = new Set(Array.from(taken, (n) => n.trim()));
  const trimmed = base.trim();
  if (!trimmed || !used.has(trimmed)) return trimmed;

  // Starts at 2: the name already there is the first of its kind.
  let n = 2;
  while (used.has(`${trimmed} (${n})`)) n += 1;
  return `${trimmed} (${n})`;
};

/** True when this name would collide with one already on the norm set. */
export const isNameTaken = (name: string, taken: Iterable<string>): boolean => {
  const trimmed = name.trim();
  if (!trimmed) return false;
  return Array.from(taken, (n) => n.trim()).includes(trimmed);
};

/**
 * A sentence for the unique violation, when one gets past the check above.
 *
 * Still reachable: another tab, or someone else on the same norm set, can take
 * the name between this form loading and being saved. Postgres reports it by
 * constraint name, which says nothing to the person who typed it.
 */
export const saveErrorMessage = (message: string, name: string): string =>
  DUPLICATE.test(message)
    ? `A translation called “${name.trim()}” already exists on this norm set. Rename this one — the same audience over a different set of products is fine, it just needs its own name.`
    : message;
